"use strict";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Flex, Text, TouchableArea, isWeb } from "ui/src";
import { useCurrencyInfosToTokenOptions } from "components/TokenSelector/hooks/useCurrencyInfosToTokenOptions";
import { useTrendingTokensCurrencyInfos } from "components/TokenSelector/hooks/useTrendingTokensCurrencyInfos";
import { OnchainItemSectionName } from "components/lists/OnchainItemList/types";
import { useNftSearchResultsToNftCollectionOptions } from "components/lists/items/nfts/useNftSearchResultsToNftCollectionOptions";
import { useFavoriteWalletOptions } from "components/lists/items/wallets/useFavoriteWalletOptions";
import { useOnchainItemListSection } from "components/lists/utils";
import { useSearchPopularNftCollectionsQuery } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { SearchModalList } from "features/search/SearchModal/SearchModalList";
import {
  NUMBER_OF_RESULTS_LONG,
  NUMBER_OF_RESULTS_MEDIUM,
  NUMBER_OF_RESULTS_SHORT
} from "features/search/SearchModal/constants";
import { useRecentlySearchedOptions } from "features/search/SearchModal/hooks/useRecentlySearchedOptions";
import { SearchTab } from "features/search/SearchModal/types";
import { useTrendingPoolsOptions } from "features/search/SearchModal/hooks/useTrendingPoolsOptions";
import { clearSearchHistory } from "features/search/searchHistorySlice";
import noop from "utilities/src/react/noop";
function ClearButton({ onPress }) {
  const { t } = useTranslation();
  return <Flex height="100%" justifyContent="center" alignItems="center">
      <TouchableArea onPress={onPress}>
        <Text color="$neutral2" variant="buttonLabel3">
          {t("common.clear")}
        </Text>
      </TouchableArea>
    </Flex>;
}
function useSectionsForNoQuerySearch({
  chainFilter,
  activeTab
}) {
  const dispatch = useDispatch();
  const recentlySearchedOptions = useRecentlySearchedOptions({
    chainFilter,
    activeTab,
    numberOfRecentSearchResults: NUMBER_OF_RESULTS_SHORT
  });
  const onPressClearSearchHistory = useCallback(() => {
    dispatch(clearSearchHistory());
  }, [dispatch]);
  const recentSearchSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.RecentSearches,
    options: recentlySearchedOptions,
    endElement: <ClearButton onPress={onPressClearSearchHistory} />
  });
  const numberOfTrendingTokens = activeTab === SearchTab.All ? NUMBER_OF_RESULTS_MEDIUM : NUMBER_OF_RESULTS_LONG;
  const skipTrendingTokensQuery = activeTab !== SearchTab.Tokens && activeTab !== SearchTab.All;
  const {
    data: tokens,
    error: tokensError,
    refetch: refetchTokens,
    loading: loadingTokens
  } = useTrendingTokensCurrencyInfos(chainFilter, skipTrendingTokensQuery);
  const trendingTokenOptions = useCurrencyInfosToTokenOptions({ currencyInfos: tokens });
  const trendingTokenSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.TrendingTokens,
    options: trendingTokenOptions?.slice(0, numberOfTrendingTokens)
  });
  const skipPopularNftsQuery = isWeb || activeTab !== SearchTab.NFTCollections && activeTab !== SearchTab.All;
  const {
    data: popularNfts,
    loading: loadingPopularNfts,
    error: popularNftsError
  } = useSearchPopularNftCollectionsQuery({ skip: skipPopularNftsQuery });
  const popularNftOptions = useNftSearchResultsToNftCollectionOptions(popularNfts, chainFilter);
  const popularNftSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.PopularNFTCollections,
    options: popularNftOptions
  });
  const favoriteWalletsOptions = useFavoriteWalletOptions({ skip: activeTab !== SearchTab.Wallets });
  const favoriteWalletsSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.FavoriteWallets,
    options: favoriteWalletsOptions
  });
  const numberOfTrendingPools = activeTab === SearchTab.Pools ? NUMBER_OF_RESULTS_LONG : NUMBER_OF_RESULTS_MEDIUM;
  const skipTrendingPoolsQuery = activeTab !== SearchTab.Pools && activeTab !== SearchTab.All;
  const {
    data: trendingPoolOptions,
    loading: loadingTrendingPools,
    error: trendingPoolsError,
    refetch: refetchTrendingPools
  } = useTrendingPoolsOptions(numberOfTrendingPools, chainFilter, !skipTrendingPoolsQuery);
  const trendingPoolsSection = useOnchainItemListSection({
    sectionKey: OnchainItemSectionName.TrendingPools,
    options: skipTrendingPoolsQuery ? [] : trendingPoolOptions
  });
  return useMemo(() => {
    let sections = [];
    switch (activeTab) {
      case SearchTab.Tokens:
        sections = [...recentSearchSection ?? [], ...trendingTokenSection ?? []];
        return {
          data: sections,
          loading: loadingTokens,
          error: tokensError,
          refetch: refetchTokens
        };
      case SearchTab.Pools:
        sections = [...recentSearchSection ?? [], ...trendingPoolsSection ?? []];
        return {
          data: sections,
          loading: loadingTrendingPools,
          error: trendingPoolsError,
          refetch: refetchTrendingPools
        };
      case SearchTab.Wallets:
        return {
          data: [...recentSearchSection ?? [], ...favoriteWalletsSection ?? []],
          loading: false,
          error: void 0,
          refetch: noop
        };
      case SearchTab.NFTCollections:
        return {
          data: [...recentSearchSection ?? [], ...popularNftSection ?? []],
          // add recent nft searches
          loading: loadingPopularNfts,
          error: popularNftsError,
          refetch: noop
        };
      default:
      case SearchTab.All:
        if (isWeb) {
          sections = [...recentSearchSection ?? [], ...trendingTokenSection ?? [], ...trendingPoolsSection ?? []];
        } else {
          sections = [...recentSearchSection ?? [], ...trendingTokenSection ?? [], ...popularNftSection ?? []];
        }
        return {
          data: sections,
          loading: loadingTokens || loadingTrendingPools,
          error: tokensError || trendingPoolsError,
          refetch: () => {
            refetchTokens();
            refetchTrendingPools();
          }
        };
    }
  }, [
    activeTab,
    favoriteWalletsSection,
    loadingPopularNfts,
    loadingTokens,
    loadingTrendingPools,
    popularNftSection,
    popularNftsError,
    recentSearchSection,
    refetchTokens,
    refetchTrendingPools,
    tokensError,
    trendingPoolsError,
    trendingTokenSection,
    trendingPoolsSection
  ]);
}
export const SearchModalNoQueryList = memo(function _SearchModalNoQueryList({
  chainFilter,
  activeTab,
  onSelect
}) {
  const { t } = useTranslation();
  const { data: sections, loading, error, refetch } = useSectionsForNoQuerySearch({ chainFilter, activeTab });
  return <SearchModalList
    errorText={t("token.selector.search.error")}
    hasError={Boolean(error)}
    loading={loading}
    refetch={refetch}
    sections={sections}
    searchFilters={{
      searchChainFilter: chainFilter,
      searchTabFilter: activeTab
    }}
    onSelect={onSelect}
  />;
});
